import { ChangeEventHandler, MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { OnDragEndResponder } from '@hello-pangea/dnd';
import axios from 'axios';
import _ from 'lodash';

import { BannersHeader } from '../BannersHeader/BannersHeader';
import BannersTable from '../BannersTable/BannersTable';
import { Loading, Modal, Section } from '../../shared';

import { useBanners } from '../../hooks/banners';

import { handleError, moveListItem } from '../../utils/utils';
import {
    filterBannersByLocation,
    filterBannersBySearchString,
    filterBannersByStatus,
    moveFilteredListItem,
} from '../utils';

import { RouteTo } from '../../routes';

import { BannerPositionScene } from '../../types/enums/bannerType.enum';

import { Banner } from '../../types/bannerTypes';

export function Banners() {
    const navigate = useNavigate();
    const [banners, setBanners] = useState<Banner[]>([]);
    const {
        banners: fetchedBanners,
        errorMessage,
        fetch,
        isLoading,
    } = useBanners();

    const [filterByStatus, setFilterByStatus] = useState(
        localStorage.getItem('bannersFilterByStatus') ?? 'all',
    );
    const [filterByLocation, setFilterByLocation] = useState(
        localStorage.getItem('bannersFilterByLocation') ??
            BannerPositionScene.START_SCREEN,
    );
    const [searchString, setSearchString] = useState(
        localStorage.getItem('bannersSearchString') ?? '',
    );

    useEffect(() => {
        fetch();
    }, []);

    useEffect(() => {
        setBanners(fetchedBanners);
    }, [fetchedBanners]);

    const onFilterChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const { name, value } = e.target;
        if (name === 'location') {
            setFilterByLocation(value);
        }
        if (name === 'status') {
            setFilterByStatus(value);
        }
        const itemKey =
            'bannersFilterBy' +
            name.charAt(0).toUpperCase() +
            name.substring(1);
        localStorage.setItem(itemKey, value);
    };

    const onSearchStringChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value;
        localStorage.setItem('bannersSearchString', value);
        setSearchString(value);
    };

    const redirectToBannerDetails = (id: Banner['id']) => {
        navigate(RouteTo.BannerEdit(String(id)));
    };
    const copyBanner = () => undefined;
    const deleteBanner = async (
        e: MouseEvent<HTMLButtonElement>,
        bannerId: Banner['id'],
    ) => {
        e.stopPropagation();

        const isConfirmed = await Modal.confirm({
            heading: 'Delete banner',
            text: 'Really delete this banner?',
            okLabel: 'Yes do it!',
        });

        if (!isConfirmed) return;

        try {
            await axios.delete('/api/banners/v2/' + bannerId);

            await fetch();
        } catch (error) {
            handleError('Error deleting banner', error);
        }
    };

    const saveBannersOrder = async (ids: number[]) => {
        try {
            await axios.put('/api/banners/v2/priorities', { ids });
        } catch (error) {
            handleError('Error reordering banners', error, true);
        }
    };

    const onDragEnd: OnDragEndResponder = async (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        try {
            // Result source and destination are indices in the filtered list,
            // so there is some work to match them to positions in the original (unfiltered) list
            const [sourceId, destinationId] = moveFilteredListItem<Banner>(
                banners,
                result.source.index,
                result.destination.index,
                (banner: Banner) => banner.positionScene === filterByLocation,
            );

            const reorderedBanners = moveListItem(
                banners,
                sourceId,
                destinationId,
            );
            setBanners(reorderedBanners);

            const ids = reorderedBanners.map((banner) => banner.id);
            await saveBannersOrder(ids);
            // Fetch updated list of banners
            await fetch();
        } catch (error) {
            handleError("Couldn't reorder banners", error, true);
        }
    };

    const isDragDropDisabled =
        isLoading || !!searchString || filterByStatus !== 'all';

    if (isLoading && !banners.length) {
        return <Loading />;
    }

    if (errorMessage) {
        return <Section>{errorMessage}</Section>;
    }

    const filterBanners = _.flow([
        (banners) => filterBannersBySearchString(banners, searchString),
        (banners) => filterBannersByLocation(banners, filterByLocation),
        (banners) => filterBannersByStatus(banners, filterByStatus),
    ]);
    const filteredBanners = filterBanners(banners);

    return (
        <>
            <Section>
                <BannersHeader
                    bannersCount={filteredBanners.length}
                    searchString={searchString}
                    onSearchStringChange={onSearchStringChange}
                    filterValues={{
                        status: filterByStatus,
                        location: filterByLocation,
                    }}
                    onFilterChange={onFilterChange}
                />
                {filteredBanners.length ? (
                    <BannersTable
                        banners={filteredBanners}
                        onBannerClick={redirectToBannerDetails}
                        onDragEnd={onDragEnd}
                        onCopyBannerClick={copyBanner}
                        onDeleteBannerClick={deleteBanner}
                        isDragDropDisabled={isDragDropDisabled}
                    />
                ) : (
                    <div>No banners</div>
                )}
            </Section>
        </>
    );
}
